import React, { useEffect, useRef, useState } from 'react';
import { Save, X, Loader2, Upload, Image as ImageIcon } from 'lucide-react';
import { UploadAPI } from '../api';

// Categories statiques (identique à mock.js CATEGORIES).
const CATEGORIES = [
  { name: 'Chroniques', slug: 'chroniques' },
  { name: 'Leçons de vie', slug: 'lecons-de-vie' },
  { name: 'Fables', slug: 'fables' },
  { name: 'Art de vivre', slug: 'art-de-vivre' },
];

// Slugify simple (garder synchro avec backend/stories_routes.py:slugify)
const slugify = (v) => {
  if (!v) return '';
  let s = v.toLowerCase().trim();
  s = s.replace(/[àâä]/g, 'a').replace(/[éèêë]/g, 'e').replace(/[îï]/g, 'i')
       .replace(/[ôö]/g, 'o').replace(/[ùûü]/g, 'u').replace(/[ç]/g, 'c')
       .replace(/[œ]/g, 'oe').replace(/[æ]/g, 'ae')
       .replace(/['’«»]/g, '').replace(/[—–]/g, '-');
  s = s.replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  return s.slice(0, 120) || 'story';
};

const todayDDMMYYYY = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const emptyStory = () => ({
  title: '',
  slug: '',
  category: 'Chroniques',
  category_slug: 'chroniques',
  excerpt: '',
  body: '',
  image: '',
  author: 'Bcool',
  date: todayDDMMYYYY(),
  read_time: '10 min de lecture',
  published: true,
});

const StoryForm = ({ initial, onCancel, onSubmit, submitting }) => {
  const [values, setValues] = useState(initial || emptyStory());
  const [autoSlug, setAutoSlug] = useState(!initial?.slug);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    if (initial) setValues(initial);
  }, [initial]);

  const update = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const handleFilePick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError('');
    setUploadProgress(0);
    setUploading(true);
    try {
      const result = await UploadAPI.image(file, {
        onProgress: (p) => setUploadProgress(p),
      });
      update('image', result.url);
    } catch (err) {
      setUploadError(err.payload?.detail || err.message || 'Erreur lors de l’upload.');
    } finally {
      setUploading(false);
      // reset the input so the same file can be re-selected if needed
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onTitleChange = (v) => {
    update('title', v);
    if (autoSlug) update('slug', slugify(v));
  };

  const onCategoryChange = (name) => {
    const cat = CATEGORIES.find((c) => c.name === name);
    update('category', name);
    update('category_slug', cat ? cat.slug : slugify(name));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    // Basic validation
    if (!values.title.trim() || values.title.trim().length < 3) return setError('Le titre doit faire au moins 3 caractères.');
    if (!values.slug.trim() || values.slug.trim().length < 3) return setError('Le slug est requis.');
    if (!values.excerpt.trim()) return setError('L’extrait est requis.');
    if (!values.body.trim() || values.body.trim().length < 100) return setError('Le corps doit faire au moins 100 caractères (HTML autorisé).');
    if (!values.image.trim()) return setError('L’URL de l’image est requise.');
    if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(values.date.trim())) return setError('La date doit être au format JJ/MM/AAAA.');

    onSubmit({ ...values, slug: slugify(values.slug) });
  };

  const inputCls =
    'w-full border border-gray-300 focus:border-[#8b2c3f] focus:ring-1 focus:ring-[#8b2c3f] outline-none px-3 py-2 text-[14px] text-gray-900';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* TITLE */}
      <div>
        <label className="block text-[11px] uppercase font-bold tracking-wider text-gray-700 mb-2">
          Titre <span className="text-[#8b2c3f]">*</span>
        </label>
        <textarea
          rows={2}
          value={values.title}
          onChange={(e) => onTitleChange(e.target.value)}
          className={inputCls}
          placeholder="Le titre percutant de votre histoire…"
          required
        />
      </div>

      {/* SLUG */}
      <div>
        <label className="flex items-center justify-between text-[11px] uppercase font-bold tracking-wider text-gray-700 mb-2">
          <span>Slug URL <span className="text-[#8b2c3f]">*</span></span>
          <label className="normal-case text-[11px] text-gray-500 flex items-center gap-1 tracking-normal">
            <input
              type="checkbox"
              checked={autoSlug}
              onChange={(e) => setAutoSlug(e.target.checked)}
            />
            générer depuis le titre
          </label>
        </label>
        <input
          type="text"
          value={values.slug}
          onChange={(e) => { setAutoSlug(false); update('slug', e.target.value); }}
          className={`${inputCls} font-mono text-[13px]`}
          placeholder="mon-histoire-percutante"
          required
        />
        <p className="mt-1 text-[11px] text-gray-500">L&apos;URL sera : <code>/article/{values.slug || 'mon-histoire'}</code></p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* CATEGORY */}
        <div>
          <label className="block text-[11px] uppercase font-bold tracking-wider text-gray-700 mb-2">Catégorie</label>
          <select
            value={values.category}
            onChange={(e) => onCategoryChange(e.target.value)}
            className={inputCls}
          >
            {CATEGORIES.map((c) => (
              <option key={c.slug} value={c.name}>{c.name}</option>
            ))}
          </select>
        </div>
        {/* AUTHOR */}
        <div>
          <label className="block text-[11px] uppercase font-bold tracking-wider text-gray-700 mb-2">Auteur</label>
          <input type="text" value={values.author} onChange={(e) => update('author', e.target.value)} className={inputCls} />
        </div>
        {/* DATE */}
        <div>
          <label className="block text-[11px] uppercase font-bold tracking-wider text-gray-700 mb-2">Date (JJ/MM/AAAA)</label>
          <input type="text" value={values.date} onChange={(e) => update('date', e.target.value)} className={inputCls} placeholder="16/07/2026" />
        </div>
      </div>

      {/* READ TIME + PUBLISHED */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-[11px] uppercase font-bold tracking-wider text-gray-700 mb-2">Temps de lecture</label>
          <input type="text" value={values.read_time} onChange={(e) => update('read_time', e.target.value)} className={inputCls} placeholder="10 min de lecture" />
        </div>
        <div className="flex items-end pb-1">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={values.published} onChange={(e) => update('published', e.target.checked)} />
            <span className="font-semibold">Publié (visible sur le site)</span>
          </label>
        </div>
      </div>

      {/* IMAGE */}
      <div>
        <label className="block text-[11px] uppercase font-bold tracking-wider text-gray-700 mb-2">
          Image de l&apos;histoire <span className="text-[#8b2c3f]">*</span>
        </label>

        {/* Bouton upload + input caché */}
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <button
            type="button"
            onClick={handleFilePick}
            disabled={uploading}
            className="inline-flex items-center gap-2 bg-[#8b2c3f] hover:bg-[#6d1f2f] text-white font-bold text-[12px] uppercase tracking-widest px-5 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {uploading ? (
              <><Loader2 size={15} className="animate-spin" /> Envoi… {uploadProgress}%</>
            ) : (
              <><Upload size={15} /> Choisir depuis mon ordinateur</>
            )}
          </button>
          <span className="text-[11px] text-gray-500">JPG, PNG, WebP — max 10 Mo</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {uploading && (
          <div className="w-full bg-gray-100 h-2 mb-3 overflow-hidden">
            <div
              className="h-full bg-[#8b2c3f] transition-all duration-200"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        )}

        {uploadError && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2 mb-3">
            {uploadError}
          </div>
        )}

        {/* Champ URL (auto-rempli après upload, ou modifiable manuellement) */}
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500 mb-1">
          <ImageIcon size={12} /> URL / chemin de l&apos;image
        </div>
        <input
          type="text"
          value={values.image}
          onChange={(e) => update('image', e.target.value)}
          className={`${inputCls} font-mono text-[13px]`}
          placeholder="/images/story-xxx.jpg  ou  https://…"
          required
        />
        {values.image && (
          <div className="mt-3 border border-gray-200 bg-gray-50 aspect-[16/9] w-full max-w-md overflow-hidden">
            <img src={values.image} alt="" className="w-full h-full object-contain" />
          </div>
        )}
      </div>

      {/* EXCERPT */}
      <div>
        <label className="block text-[11px] uppercase font-bold tracking-wider text-gray-700 mb-2">
          Extrait / chapeau <span className="text-[#8b2c3f]">*</span>
        </label>
        <textarea
          rows={3}
          value={values.excerpt}
          onChange={(e) => update('excerpt', e.target.value)}
          className={inputCls}
          placeholder="Le petit paragraphe accrocheur affiché dans les cartes et en tête d’article."
          required
        />
      </div>

      {/* BODY */}
      <div>
        <label className="block text-[11px] uppercase font-bold tracking-wider text-gray-700 mb-2">
          Corps de l&apos;article (HTML) <span className="text-[#8b2c3f]">*</span>
        </label>
        <textarea
          rows={18}
          value={values.body}
          onChange={(e) => update('body', e.target.value)}
          className={`${inputCls} font-mono text-[13px] leading-relaxed`}
          placeholder={"<h3>PARTIE 1 — Le début</h3>\n<p>Votre histoire commence…</p>"}
          required
        />
        <p className="mt-1 text-[11px] text-gray-500">
          Vous pouvez utiliser du HTML : <code>&lt;h3&gt;</code>, <code>&lt;p&gt;</code>, <code>&lt;em&gt;</code>, <code>&lt;strong&gt;</code>, etc.
        </p>
      </div>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 bg-gray-900 hover:bg-[#8b2c3f] text-white font-bold text-[12px] uppercase tracking-widest px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
          {submitting ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 hover:bg-gray-100 font-bold text-[12px] uppercase tracking-widest px-6 py-3 transition-colors"
        >
          <X size={15} /> Annuler
        </button>
      </div>
    </form>
  );
};

export default StoryForm;
